import { ChatPeopleTablePanel } from "@/components/chat/chat-people-table-panel";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useExitTransition } from "@/hooks/useExitTransition";
import { cn } from "@/lib/utils";
import {
  ChatSidePanel,
  chatArtifactPanelStore,
  useChatSidePanel,
} from "@/stores/chat-artifact-panel-store";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  usePanelRef,
  type Layout,
  type LayoutChangedMeta,
} from "react-resizable-panels";
import { ChatArtifactPanel } from "./chat-artifact-panel";

/**
 * How long the panel takes to open or close. Matches the `300ms` of the
 * `[data-artifact-split-animating]` rule in `styles/animations.css` and the
 * `duration-300` of the panel's own enter/exit classes below.
 */
export const ARTIFACT_PANEL_TRANSITION_MS = 300;

const ARTIFACT_PANEL_ID = "chat-artifact";

type SidePanelKind = ChatSidePanel["kind"];

/**
 * Width the panel opens at the first time, before the user resizes it: a
 * document reads in a column, a table of people needs most of the width.
 */
const DEFAULT_PANEL_SIZES: Record<SidePanelKind, string> = {
  artifact: "35%",
  peopleTable: "60%",
};

/** Narrowest the panel may be, beside the conversation. */
const ARTIFACT_PANEL_MIN_WIDTH_PX = 320;

/** Share of the split the conversation always keeps. */
const CONVERSATION_MIN_SHARE = 0.3;

/** Narrowest the conversation stays readable at, beside an open panel. */
const CONVERSATION_MIN_READABLE_WIDTH_PX = 480;

/**
 * Narrowest split the panel opens beside the conversation in. Below it the
 * document is laid over the conversation instead, as on a phone: side by
 * side, the conversation would be left a column too narrow to read — and
 * under `ARTIFACT_PANEL_MIN_WIDTH_PX / (1 - CONVERSATION_MIN_SHARE)` the
 * library would not open the panel at all, leaving the document marked as
 * shown with nothing on screen. The chat docked beside a company page
 * (30% of the page) is below it on all but the widest screens.
 */
const MIN_SPLIT_WIDTH_PX =
  ARTIFACT_PANEL_MIN_WIDTH_PX + CONVERSATION_MIN_READABLE_WIDTH_PX;

type ChatArtifactSplitProps = {
  /** The conversation. Stays mounted whether or not a panel is open. */
  readonly children: ReactNode;
  /** The conversation shown, which the panel's PDF download is scoped to. */
  readonly conversationId: string;
};

/** What the panel shows, keyed so switching to another one replays the entrance. */
const sidePanelKey = (panel: ChatSidePanel | null): string | undefined => {
  if (!panel) return undefined;
  return panel.kind === "artifact"
    ? `artifact-${panel.artifact.artifactId}`
    : `people-${panel.searchId}`;
};

type SidePanelContentProps = {
  readonly panel: ChatSidePanel | null;
  readonly conversationId: string;
  readonly className?: string;
  readonly dismissAs?: "close" | "back";
};

/** A document or a table of people, whichever the panel shows. */
function SidePanelContent({ panel, ...props }: SidePanelContentProps) {
  if (panel?.kind === "peopleTable") {
    return (
      <ChatPeopleTablePanel
        searchId={panel.searchId}
        live={panel.live}
        className={props.className}
        dismissAs={props.dismissAs}
      />
    );
  }
  return (
    <ChatArtifactPanel
      artifact={panel?.kind === "artifact" ? panel.artifact : null}
      {...props}
    />
  );
}

/**
 * Desktop split between the conversation and the side panel: a document
 * (an Artifact) or a table of people, one at a time.
 *
 * The group is always rendered and the panel only collapses to zero: were
 * the split mounted only while an Artifact is open, React would see a new
 * root around the conversation and rebuild it from scratch — scroll
 * position, composer draft and all — every time the panel opened or closed,
 * mid-stream included, since the panel opens itself as an answer arrives.
 *
 * Opening and closing animate the panel's width (a `flex-grow` transition,
 * enabled only for that moment so dragging the handle stays immediate) and
 * slide its content in and out.
 */
export function ChatArtifactSplit({
  children,
  conversationId,
}: ChatArtifactSplitProps) {
  const activePanel = useChatSidePanel();
  const { displayed, isExiting } = useExitTransition(
    activePanel,
    ARTIFACT_PANEL_TRANSITION_MS,
  );
  const groupElementRef = useRef<HTMLDivElement>(null);
  // Unknown until first measured, and read as wide: the split is the layout
  // the conversation is mounted in either way.
  const [groupWidth, setGroupWidth] = useState<number | null>(null);
  useEffect(() => {
    const group = groupElementRef.current;
    if (!group) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setGroupWidth(entry.contentRect.width);
    });
    observer.observe(group);
    return () => observer.disconnect();
  }, []);
  const isOverlay = groupWidth !== null && groupWidth < MIN_SPLIT_WIDTH_PX;
  // Open as a side panel; an overlaid document leaves the split collapsed.
  const isOpen = activePanel !== null && !isOverlay;
  const panelKind: SidePanelKind = activePanel?.kind ?? "artifact";

  const artifactPanelRef = usePanelRef();
  // The width the user last left each kind of panel at, restored on the next
  // open of that kind.
  const lastOpenSizesRef = useRef<Record<SidePanelKind, string>>({
    ...DEFAULT_PANEL_SIZES,
  });
  // What the open panel was sized for: a table replacing a document widens
  // it, and the other way round.
  const sizedKindRef = useRef<SidePanelKind | null>(null);
  const panelKindRef = useRef(panelKind);
  panelKindRef.current = panelKind;

  useEffect(() => {
    const group = groupElementRef.current;
    const panel = artifactPanelRef.current;
    if (!group || !panel) return;

    let isCollapsed: boolean;
    try {
      isCollapsed = panel.isCollapsed();
    } catch (error) {
      // Thrown only before the group has computed its first layout; the
      // panel then keeps the size it was declared with, which is correct.
      console.warn("Artifact panel not laid out yet", error);
      return;
    }
    const isResized =
      isOpen && !isCollapsed && sizedKindRef.current !== panelKind;
    sizedKindRef.current = isOpen ? panelKind : null;
    if (isOpen !== isCollapsed && !isResized) return;

    // Set on the element directly rather than through React state: the
    // library applies the new size synchronously, and the transition has to
    // be in place before that happens or the change is not animated.
    group.dataset.artifactSplitAnimating = "";
    if (isOpen) {
      panel.resize(lastOpenSizesRef.current[panelKind]);
    } else {
      panel.collapse();
    }
    const timer = window.setTimeout(() => {
      delete group.dataset.artifactSplitAnimating;
    }, ARTIFACT_PANEL_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [isOpen, panelKind, artifactPanelRef]);

  // Only what the user did with the handle. The library also reports the
  // panel's size on mount, on re-renders and while it opens — still 0 at
  // that point — so reacting to every report would close the panel the
  // instant it was asked to open.
  const handleLayoutChanged = (
    layout: Layout,
    { isUserInteraction }: LayoutChangedMeta,
  ): void => {
    if (!isUserInteraction) return;
    const artifactPanelSize = layout[ARTIFACT_PANEL_ID] ?? 0;
    if (artifactPanelSize > 0) {
      lastOpenSizesRef.current[panelKindRef.current] = `${artifactPanelSize}%`;
      return;
    }
    // Dragged shut: close it for real, so the inline card and the auto-open
    // logic see the same state as a click on Close.
    if (chatArtifactPanelStore.getPanelSnapshot()) {
      chatArtifactPanelStore.close();
    }
  };

  // Shown in one place only: the side panel when the split has room, laid
  // over the conversation when it does not.
  const sidePanel = isOverlay ? null : displayed;

  return (
    <>
      <ResizablePanelGroup
        // While the handle is dragged, the pointer crosses the Artifact's
        // iframe, which swallows every move over it as a separate document —
        // the drag stalls the moment it enters the panel. Letting pointer
        // events through the iframe for that time keeps them on the handle.
        className="absolute w-full h-full [&:has([data-separator=active])_iframe]:pointer-events-none"
        elementRef={groupElementRef}
        onLayoutChanged={handleLayoutChanged}
        orientation="horizontal"
      >
        <ResizablePanel
          className="relative"
          id="chat-conversation"
          minSize={`${CONVERSATION_MIN_SHARE * 100}%`}
        >
          {children}
        </ResizablePanel>
        <ResizableHandle
          className={cn(
            "transition-opacity duration-300",
            !sidePanel && "pointer-events-none opacity-0",
          )}
          disabled={!isOpen}
          withHandle
        />
        <ResizablePanel
          className="relative"
          collapsedSize="0%"
          collapsible
          defaultSize={isOpen ? DEFAULT_PANEL_SIZES[panelKind] : "0%"}
          id={ARTIFACT_PANEL_ID}
          maxSize="65%"
          minSize={`${ARTIFACT_PANEL_MIN_WIDTH_PX}px`}
          panelRef={artifactPanelRef}
        >
          <SidePanelContent
            // Keyed on what it shows so switching to another one replays the
            // entrance instead of swapping the content in place.
            key={sidePanelKey(sidePanel)}
            panel={sidePanel}
            conversationId={conversationId}
            className={cn(
              // Reduced motion keeps a short fade and drops the slide.
              "duration-300 motion-reduce:duration-150",
              isExiting
                ? "animate-out fade-out ease-in fill-mode-forwards motion-safe:slide-out-to-right-4"
                : "animate-in fade-in ease-out motion-safe:slide-in-from-right-4",
            )}
          />
        </ResizablePanel>
      </ResizablePanelGroup>
      {isOverlay && displayed && (
        // Above the conversation, which stays mounted underneath: closing the
        // document gives back the thread exactly as it was left. Dropped
        // below the top edge over a dimmed thread, as a sheet, so it reads as
        // laid on the conversation rather than as a page that replaced it.
        <div
          className={cn(
            "absolute inset-0 z-20 flex flex-col bg-black/30 pt-6 duration-300 motion-reduce:duration-150",
            isExiting
              ? "animate-out fade-out ease-in fill-mode-forwards"
              : "animate-in fade-in ease-out",
          )}
          data-testid="chat-artifact-overlay"
        >
          <SidePanelContent
            key={sidePanelKey(displayed)}
            panel={displayed}
            conversationId={conversationId}
            dismissAs="back"
            className={cn(
              "min-h-0 flex-1 overflow-hidden rounded-t-xl border shadow-lg duration-300 motion-reduce:duration-150",
              isExiting
                ? "animate-out ease-in fill-mode-forwards motion-safe:slide-out-to-bottom-4"
                : "animate-in ease-out motion-safe:slide-in-from-bottom-4",
            )}
          />
        </div>
      )}
    </>
  );
}
